import React, { useState, useCallback } from 'react';
import { Icon, Popup, Message } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';

type CaptureError = 'permission_denied' | 'generic' | null;

interface WebSnipperProps {
  onImageReady: (imageFiles: string[]) => void;
}
const supportsCaptureController =
  Boolean(navigator.mediaDevices?.getDisplayMedia) && 'CaptureController' in window;

/**
 * WebSnipper component for capturing screenshots in web browsers.
 * Uses the browser's getDisplayMedia API to allow users to select
 * a screen, window, or tab to capture.
 */
function WebSnipper({ onImageReady }: WebSnipperProps) {
  const [error, setError] = useState<CaptureError>(null);

  const captureScreen = useCallback(async (): Promise<void> => {
    setError(null);

    let stream: MediaStream | null = null;
    let video: HTMLVideoElement | null = null;
    let canvas: HTMLCanvasElement | null = null;

    try {
      // CaptureController is required to avoid losing focus when capturing the screen
      const controller = supportsCaptureController ? new CaptureController() : undefined;
      controller?.setFocusBehavior('no-focus-change');

      stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'never',
        },
        audio: false,
        controller,
      });

      video = document.createElement('video');
      video.srcObject = stream;
      video.style.cssText = 'position:absolute;top:-10000px;left:-10000px;';
      document.body.appendChild(video);
      await video.play();

      canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Unable to acquire canvas context');
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const base64Image = canvas.toDataURL('image/png');
      onImageReady([base64Image]);
      try {
        if (!supportsCaptureController) {
          window.focus();
        }
      } catch (error) {
        console.warn('Failed to refocus window after screen capture', error);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('permission_denied');
          return;
        }

        if (err.name === 'AbortError') {
          setError(null);
          return;
        }
      }

      setError('generic');
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      video?.remove();
      canvas?.remove();
    }
  }, [onImageReady]);

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <div className="web-snipper-container">
      {error && (
        <Message warning onDismiss={dismissError}>
          <Message.Header>
            <FormattedMessage id="snipper.web.error.title" />
          </Message.Header>
          <p>
            {error === 'permission_denied' ? (
              <FormattedMessage id="snipper.web.error.permission_denied" />
            ) : (
              <FormattedMessage id="snipper.web.error.generic" />
            )}
          </p>
        </Message>
      )}
      <Popup
        content={<FormattedMessage id="snipper.button.tooltip" />}
        position="right center"
        trigger={
          <button type="button" className="button snipper picoxia" onClick={captureScreen}>
            <Icon name="cut" />
            <span>
              <FormattedMessage id="snipper.button" />
            </span>
          </button>
        }
      />
    </div>
  );
}

export default React.memo(WebSnipper);
